/* Breakz — wishlist.
 *
 * One piece of logic for every surface: the button on the product image,
 * the header counter, the drawer and the standalone page. The file is
 * shared on purpose — split it across sections and the lists start to
 * diverge. For the same reason the theme has one product card everywhere.
 *
 * Storage is localStorage. There are no accounts in Phase 1 by agreed
 * decision, and creating a customer just because someone tapped a heart
 * is not acceptable: it would smuggle marketing consent into a place
 * where nobody gave it. The honest price: the list does not travel
 * between devices.
 *
 * The row markup comes from Liquid: /products/<handle>?section_id=breakz-wishlist-row
 * (sections/breakz-wishlist-row.liquid, through the Section Rendering API).
 * The stock .js returns no metafields, and without them there is no
 * "2023 · Mint" caption, the same one the cart drawer shows. Building it
 * on the client would mean a second implementation beside a working one.
 *
 * Here too is the store-wide ban on two open drawers: cart and wishlist
 * would otherwise overlap.
 */
(function () {
  var KEY = 'bx-wishlist';

  function read() {
    try {
      var v = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(v) ? v : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {
      /* private mode — we simply will not remember */
    }
  }

  function toggle(handle) {
    var list = read();
    var at = list.indexOf(handle);
    if (at > -1) list.splice(at, 1);
    else list.push(handle);
    write(list);
  }

  function drop(handle) {
    write(
      read().filter(function (h) {
        return h !== handle;
      })
    );
  }

  /* ── Reflecting the state ─────────────────────────────────────── */

  function syncButtons() {
    var list = read();
    document.querySelectorAll('[data-bx-wishlist]').forEach(function (b) {
      b.setAttribute('aria-pressed', list.indexOf(b.dataset.bxWishlist) > -1 ? 'true' : 'false');
    });
  }

  function syncCount() {
    var n = read().length;
    document.querySelectorAll('[data-bx-wl-count]').forEach(function (el) {
      el.textContent = n;
      /* Hidden at zero rather than showing a zero: an empty counter on
         the icon reads as a bug. */
      el.hidden = n === 0;
    });
  }

  /* ── List ─────────────────────────────────────────────────────── */

  function renderInto(host) {
    var grid = host.querySelector('[data-bx-wl-grid]');
    var empty = host.querySelector('[data-bx-wl-empty]');
    if (!grid) return;

    var list = read();
    grid.textContent = '';

    if (!list.length) {
      grid.hidden = true;
      if (empty) empty.hidden = false;
      return;
    }

    if (empty) empty.hidden = true;
    grid.hidden = false;

    /* Slots are laid out immediately and filled as answers arrive.
       Otherwise rows land in network order and the list reshuffles on
       every open. The placeholder holds the height — no jumps. */
    list.forEach(function (handle) {
      var slot = document.createElement('div');
      slot.className = 'bxwl-slot';
      grid.appendChild(slot);

      fetch('/products/' + encodeURIComponent(handle) + '?section_id=breakz-wishlist-row')
        .then(function (r) {
          /* The product was deleted or unpublished — we drop it quietly.
             A broken row is worse than a missing one. */
          if (!r.ok) {
            drop(handle);
            slot.remove();
            syncCount();
            return null;
          }
          return r.text();
        })
        .then(function (html) {
          if (html == null) return;
          var tmp = document.createElement('div');
          tmp.innerHTML = html.trim();
          // The section arrives in its shopify-section wrapper; the row is inside.
          var row = tmp.querySelector('.bxwl-row');
          if (row) slot.replaceWith(row);
          else slot.remove();
        })
        .catch(function () {
          slot.remove();
        });
    });
  }

  function refresh() {
    syncButtons();
    syncCount();
    document.querySelectorAll('[data-bx-wl-host]').forEach(renderInto);
  }

  /* ── Wiring ───────────────────────────────────────────────────
     Delegation from the document rather than listeners on nodes: theme
     web components rebuild the markup after parsing, and handlers on the
     nodes disappear with them. On top of that, list rows arrive from the
     network later. */

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;

    var add = e.target.closest('[data-bx-wishlist]');
    if (add) {
      toggle(add.dataset.bxWishlist);
      refresh();
      return;
    }

    var rm = e.target.closest('[data-bx-wl-drop]');
    if (rm) {
      drop(rm.dataset.bxWlDrop);
      refresh();
    }
  });

  document.addEventListener('DOMContentLoaded', refresh);
  /* Return from the browser cache: the state may have changed in another
     tab while the page is served as it was. */
  window.addEventListener('pageshow', refresh);
  /* The list was changed in a neighbouring tab. */
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) refresh();
  });

  /* The catalog repaints itself on every filter and sort change: the
     section is fetched and morphed into the page, and the fresh hearts
     arrive with aria-pressed="false" no matter what is actually saved.
     The theme fires no event we could hook, so we watch the DOM and
     re-read the state whenever new hearts turn up.

     Buttons only, not the whole refresh: a section render touches neither
     the counter in the header nor the drawer contents, and repainting
     those on every filter click would be work for nothing. */
  if (typeof MutationObserver === 'function') {
    var resyncQueued = false;

    var queueResync = function () {
      if (resyncQueued) return;
      resyncQueued = true;
      requestAnimationFrame(function () {
        resyncQueued = false;
        syncButtons();
      });
    };

    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches('[data-bx-wishlist]') || node.querySelector('[data-bx-wishlist]')) {
            queueResync();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ── One drawer at a time ─────────────────────────────────────
     A store-wide rule, not a wishlist one: the theme component can hold
     several drawers open and stacks them by z-index, but visually they
     overlap. When one opens, we close the rest.

     The event name comes from the component: theme-drawer:open. */
  document.addEventListener('theme-drawer:open', function (e) {
    var opened = e.target;

    document.querySelectorAll('theme-drawer').forEach(function (d) {
      if (d !== opened && d.isOpen && typeof d.close === 'function') d.close();
    });

    /* The list may have grown from another page while the drawer was
       closed. We repaint its contents only. */
    if (opened.querySelector && opened.querySelector('[data-bx-wl-host]')) {
      opened.querySelectorAll('[data-bx-wl-host]').forEach(renderInto);
    }
  });

  window.BreakzWishlist = {
    read: read,
    count: function () {
      return read().length;
    },
    refresh: refresh,
  };

  refresh();
})();
